import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { AssetCategory, AssetDto } from '../../sj-orm/models/asset.dto';
import {
  CommodityAssetTokenDetailsType,
  CommodityAssetTokenGeneralType,
} from '../../sj-orm/models/assets/categories';
import {
  BaseAssetType,
  IssuerAndExchangePlatformType,
  PlaceOfRecord,
} from '../../sj-orm/models/assets/enums';
import { ContactDto, ContactType } from '../../sj-orm/models/contact.dto';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type CommodityAssetToken = CommodityAssetTokenFormGeneralType &
  CommodityAssetTokenFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const BaseAssetTypesList = [
  { label: 'forms_assets:commodity.enums.type.gold', key: BaseAssetType.gold },
  {
    label: 'forms_assets:commodity.enums.type.diamonds',
    key: BaseAssetType.diamonds,
  },
  {
    label: 'forms_assets:commodity.enums.type.iridium',
    key: BaseAssetType.iridium,
  },
  {
    label: 'forms_assets:commodity.enums.type.rhodium',
    key: BaseAssetType.rhodium,
  },
  { label: 'forms_assets:commodity.enums.type.zinc', key: BaseAssetType.zinc },
  { label: 'forms_assets:commodity.enums.type.lead', key: BaseAssetType.lead },
  {
    label: 'forms_assets:commodity.enums.type.aluminium',
    key: BaseAssetType.aluminium,
  },
  {
    label: 'forms_assets:commodity.enums.type.nickel',
    key: BaseAssetType.nickel,
  },
  {
    label: 'forms_assets:commodity.enums.type.nonFerrousMetals',
    key: BaseAssetType.nonFerrousMetals,
  },
  {
    label: 'forms_assets:commodity.enums.type.platinum',
    key: BaseAssetType.platinum,
  },
  {
    label: 'forms_assets:commodity.enums.type.palladium',
    key: BaseAssetType.palladium,
  },
  {
    label: 'forms_assets:commodity.enums.type.rawMaterial',
    key: BaseAssetType.rawMaterial,
  },
  {
    label: 'forms_assets:commodity.enums.type.lumber',
    key: BaseAssetType.lumber,
  },
  {
    label: 'forms_assets:commodity.enums.type.realEstate',
    key: BaseAssetType.realEstate,
  },
  {
    label: 'forms_assets:commodity.enums.type.industrialMetals',
    key: BaseAssetType.industrialMetals,
  },
  {
    label: 'forms_assets:commodity.enums.type.chemicalProducts',
    key: BaseAssetType.chemicalProducts,
  },
  {
    label: 'forms_assets:commodity.enums.type.mineralProducts',
    key: BaseAssetType.mineralProducts,
  },
  {
    label: 'forms_assets:commodity.enums.type.rareEarthMetals',
    key: BaseAssetType.rareEarthMetals,
  },
  {
    label: 'forms_assets:commodity.enums.type.grain',
    key: BaseAssetType.grain,
  },
  {
    label: 'forms_assets:commodity.enums.type.ruthenium',
    key: BaseAssetType.ruthenium,
  },
  {
    label: 'forms_assets:commodity.enums.type.petroleum',
    key: BaseAssetType.petroleum,
  },
  {
    label: 'forms_assets:commodity.enums.type.petroleumProducts',
    key: BaseAssetType.petroleumProducts,
  },
  {
    label: 'forms_assets:commodity.enums.type.other',
    key: BaseAssetType.other,
  },
];

const IssuerAndExchangePlatformList = [
  {
    label: 'forms_assets:commodity.enums.platform.informationSystemOperator',
    key: IssuerAndExchangePlatformType.informationSystemOperator,
  },
  {
    label: 'forms_assets:commodity.enums.platform.bank',
    key: IssuerAndExchangePlatformType.bank,
  },
  {
    label: 'forms_assets:commodity.enums.platform.legalEntity',
    key: IssuerAndExchangePlatformType.legalEntity,
  },
  {
    label: 'forms_assets:commodity.enums.platform.other',
    key: IssuerAndExchangePlatformType.other,
  },
];

export const PlaceOfRecordList = [
  {
    label: 'forms_assets:commodity.enums.placeOfRecord.cryptowallet',
    key: PlaceOfRecord.cryptowallet,
  },
  {
    label: 'forms_assets:commodity.enums.placeOfRecord.financialInstitution',
    key: PlaceOfRecord.financialInstitution,
  },
  {
    label: 'forms_assets:commodity.enums.placeOfRecord.document',
    key: PlaceOfRecord.document,
  },
  {
    label: 'forms_assets:commodity.enums.placeOfRecord.other',
    key: PlaceOfRecord.other,
  },
];

type CommodityAssetTokenHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  amount: QuestionType.NUMBER;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  baseAssetType: QuestionType.SELECT_FROM_LIST;
  otherBaseAssetType?: QuestionType.TEXT;
  basicAssetSpecificity: QuestionType.TEXT;
  quantity: QuestionType.NUMBER;
  issuer: QuestionType.SELECT_FROM_LIST;
  placeOfRecord?: QuestionType.SELECT_FROM_LIST;
  placeOfRecordCryptowallet?: QuestionType.PICK_ARRAY_OF_DTO;
  placeOfRecordFinancialInstitution?: QuestionType.PICK_ARRAY_OF_DTO;
  placeOfRecordDocument?: QuestionType.PICK_ARRAY_OF_DTO;
  placeOfRecordOther?: QuestionType.TEXT_MULTILINE;
  otherIssuer: QuestionType.TEXT;
  // informationSystemOperator: QuestionType.TEXT;
  // legalEntity: QuestionType.TEXT;
  // bank: QuestionType.PICK_ARRAY_OF_DTO;
};

type CommodityAssetTokenHelpDetailsType = {
  identifierName?: QuestionType.TEXT;
  identifierNumber?: QuestionType.TEXT;
  investmentStartDate?: QuestionType.DATE_TIME;
  investmentEndDate?: QuestionType.DATE_TIME;
  contractSpecialConditions?: QuestionType.TEXT;
  isTransferableToThirdParty?: QuestionType.CHECKBOX;
  issuerLegalName?: QuestionType.TEXT;
  issuerLocation?: QuestionType.TEXT;
  headCompanyLegalName?: QuestionType.TEXT;
  basicAssetPlaceOfStorage?: QuestionType.TEXT;
  addExchangePlatform?: QuestionType.CHECKBOX;
  exchangePlatform?: QuestionType.SELECT_FROM_LIST;
  exchangePlatformInformationSystemOperator?: QuestionType.TEXT;
  exchangePlatformBank?: QuestionType.PICK_ARRAY_OF_DTO;
  exchangePlatformLegalEntity?: QuestionType.TEXT;
  otherExchangePlatform?: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type CommodityAssetTokenFormGeneralType = TDtoWrapper<
  CommodityAssetTokenGeneralType,
  CommodityAssetTokenHelpGeneralType
>;

type CommodityAssetTokenFormDetailsType = TDtoWrapper<
  CommodityAssetTokenDetailsType,
  CommodityAssetTokenHelpDetailsType
>;

const CommodityAssetTokenFormGeneral: CommodityAssetTokenFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:commodity.inputs.amount.label',
    value: 0,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:commodity.inputs.currentMarketValue.label',
    tooltip: 'forms_assets:commodity.inputs.currentMarketValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  baseAssetType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:commodity.inputs.baseAssetType.label',
    tooltip: 'forms_assets:commodity.inputs.baseAssetType.tooltip',
    value: BaseAssetType.diamonds,
    items: BaseAssetTypesList,
  },
  otherBaseAssetType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.otherBaseAssetType.label',
    value: '',
    showIf: {
      baseAssetType: {
        value: BaseAssetType.other,
      },
    },
  },
  basicAssetSpecificity: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.basicAssetSpecificity.label',
    value: '',
  },
  quantity: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:commodity.inputs.quantity.label',
    value: 0,
  },
  issuer: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:commodity.inputs.issuer.label',
    tooltip: 'forms_assets:commodity.inputs.issuer.tooltip',
    value: '',
    items: IssuerAndExchangePlatformList,
    yupScheme: yup.string().required('forms_assets:commodity.inputs.issuer.errors.required'),
  },
  placeOfRecord: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:commodity.inputs.placeOfRecord.label',
    items: PlaceOfRecordList,
  },
  placeOfRecordCryptowallet: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:commodity.inputs.placeOfRecordCryptowallet.label',
    items: [CollectionName.ASSETS],
    dtoType: [AssetCategory.CENTRALIZED_WALLET, AssetCategory.DECENTRALIZED_WALLET],
    filter: (item) =>
      [AssetCategory.CENTRALIZED_WALLET, AssetCategory.DECENTRALIZED_WALLET].includes(
        (item as AssetDto).category,
      ),
    showIf: {
      placeOfRecord: {
        value: PlaceOfRecord.cryptowallet,
      },
    },
  },
  placeOfRecordFinancialInstitution: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:commodity.inputs.placeOfRecordFinancialInstitution.label',
    colName: CollectionName.CONTACTS,
    dtoType: [ContactType.BANK, ContactType.BROKER],
    filter: (item) => [ContactType.BANK, ContactType.BROKER].includes((item as ContactDto).type),
    // value: [],
    showIf: {
      placeOfRecord: {
        value: PlaceOfRecord.financialInstitution,
      },
    },
  },
  placeOfRecordDocument: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:commodity.inputs.placeOfRecordDocument.label',
    items: [CollectionName.PRIVATE_DOCUMENTS],
    showIf: {
      placeOfRecord: {
        value: PlaceOfRecord.document,
      },
    },
  },
  placeOfRecordOther: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:commodity.inputs.placeOfRecordOther.label',
    showIf: {
      placeOfRecord: {
        value: PlaceOfRecord.other,
      },
    },
  },
  otherIssuer: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.otherIssuer.label',
    required: true,
    value: '',
    showIf: {
      issuer: {
        value: IssuerAndExchangePlatformType.other,
      },
    },
  },
};

const CommodityAssetTokenFormDetails: CommodityAssetTokenFormDetailsType = {
  identifierLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:commodity.inputs.identifierLabelInfo.label',
    order: 1,
  },
  identifierName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.identifierName.label',
    tooltip: 'forms_assets:commodity.inputs.identifierName.tooltip',
    order: 2,
  },
  identifierNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.identifierNumber.label',
    order: 3,
  },
  investmentStartDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:commodity.inputs.investmentStartDate.label',
    order: 4,
  },
  investmentEndDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:commodity.inputs.investmentEndDate.label',
    order: 5,
  },
  contractSpecialConditions: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.contractSpecialConditions.label',
    order: 6,
  },
  isTransferableToThirdParty: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:commodity.inputs.isTransferableToThirdParty.label',
    order: 7,
  },
  addExchangePlatform: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:commodity.inputs.addExchangePlatform.label',
    tooltip: 'forms_assets:commodity.inputs.addExchangePlatform.tooltip',
    order: 8,
  },
  exchangePlatform: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:commodity.inputs.exchangePlatform.label',
    items: IssuerAndExchangePlatformList,
    showIf: {
      addExchangePlatform: {
        value: true,
      },
    },
    order: 9,
  },
  // exchangePlatformInformationSystemOperator: {
  //   type: QuestionType.TEXT,
  //   label: 'forms_assets:commodity.inputs.informationSystemOperator.label',
  //   order: 10,
  //   showIf: {
  //     exchangePlatform: {
  //       value: IssuerAndExchangePlatformType.informationSystemOperator,
  //     },
  //   },
  // },
  // exchangePlatformBank: {
  //   type: QuestionType.PICK_ARRAY_OF_DTO,
  //   label: 'forms_assets:commodity.inputs.bank.label',
  //   order: 11,
  //   items: CollectionName.CONTACTS,
  //   showIf: {
  //     exchangePlatform: {
  //       value: IssuerAndExchangePlatformType.bank,
  //     },
  //   },
  //   dtoType: ContactType.BANK,
  // },
  // exchangePlatformLegalEntity: {
  //   type: QuestionType.TEXT,
  //   label: 'forms_assets:commodity.inputs.legalEntity.label',
  //   order: 12,
  //   showIf: {
  //     exchangePlatform: {
  //       value: IssuerAndExchangePlatformType.legalEntity,
  //     },
  //   },
  // },
  otherExchangePlatform: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.otherExchangePlatform.label',
    order: 13,
    showIf: {
      exchangePlatform: {
        value: IssuerAndExchangePlatformType.other,
      },
    },
  },
  issuerLabelInfo: {
    type: FormLabelItem.label,
    label: 'forms_assets:commodity.inputs.issuerLabelInfo.label',
    order: 14,
  },
  issuerLegalName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.issuerLegalName.label',
    tooltip: 'forms_assets:commodity.inputs.issuerLegalName.tooltip',
    order: 15,
  },
  issuerLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.issuerLocation.label',
    order: 16,
  },
  headCompanyLegalName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.headCompanyLegalName.label',
    order: 17,
  },
  basicAssetPlaceOfStorage: {
    type: QuestionType.TEXT,
    label: 'forms_assets:commodity.inputs.basicAssetPlaceOfStorage.label',
    order: 18,
  },
  attachments: baseAssetFields.attachments,
  // insuranceAgreement: baseAassetFields.insuranceAgreement,
};

const CommodityAssetTokenForm = [
  CommodityAssetTokenFormGeneral,
  CommodityAssetTokenFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgent',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { CommodityAssetTokenForm };
